import { TabSwitchOption } from 'components_new/molecules/TabSwitch'
import { useTranslation } from 'language/translator'
import { useAllowEmbedDashboard } from 'redux/hooks/Permissions'

import { TDashboard } from 'types/GlobalDashboard'

export type TDialogType =
  | 'delete_dashboard'
  | 'edit_dashboard_title'
  | 'embed_dashboard'
  | 'copy_dashboard'
  | 'move_dashboard'

export function useDashboardTabs(
  dashboards: TDashboard[],
  editable: boolean,
  lgUp: boolean,
  onMove: (dashboard: TDashboard, direction: number) => void,
  onDuplicate: (dashboardId: string) => void,
  handleOpenDialog: (dashboardId: string, type: TDialogType) => void
): (TabSwitchOption & { title: string })[] {
  const { t } = useTranslation()
  const allowEmbedDashboard = useAllowEmbedDashboard()

  return dashboards.map((dashboard, i) => {
    return {
      value: dashboard.id,
      title: dashboard.title,
      label: dashboard.title,
      disabledTooltip: t('dashboards', 'no_access_to_tab'),
      icon: dashboard.share_settings.is_public
        ? {
            bgcolor: 'success.background',
            name: 'PublicOutlined',
            tooltip: t('dashboards', 'external_access_public')
          }
        : dashboard.share_settings.api_access
          ? {
              bgcolor: 'secondary.background',
              name: 'VpnLock',
              tooltip: t('dashboards', 'external_access_protected')
            }
          : undefined,
      menuOptions: editable
        ? [
            {
              iconName: 'MoveDownOutlined',
              iconRotation: lgUp ? '90deg' : '180deg',
              onClick: () => onMove(dashboard, -1),
              title: t('global', lgUp ? 'move_left' : 'move_up'),
              disabled: i === 0
            },
            {
              iconName: 'MoveUpOutlined',
              iconRotation: lgUp ? '90deg' : '180deg',
              onClick: () => onMove(dashboard, 1),
              title: t('global', lgUp ? 'move_right' : 'move_down'),
              divider: true,
              disabled: i === dashboards.length - 1
            },
            {
              iconName: 'Edit',
              onClick: () =>
                handleOpenDialog(dashboard.id, 'edit_dashboard_title'),
              title: t('global', 'change_name'),
              divider: true
            },
            {
              iconName: 'Duplicate',
              onClick: () => onDuplicate(dashboard.id),
              title: t('global', 'duplicate')
            },
            {
              iconName: 'FileCopyOutlined',
              onClick: () => handleOpenDialog(dashboard.id, 'copy_dashboard'),
              title: t('global', 'copy_to') + '...'
            },
            {
              iconName: 'DriveFileMoveOutlined',
              onClick: () => handleOpenDialog(dashboard.id, 'move_dashboard'),
              title: t('global', 'move_to') + '...',
              disabled: dashboards.length === 1,
              divider: true
            },
            {
              iconName: 'CodeOutlined',
              onClick: () => handleOpenDialog(dashboard.id, 'embed_dashboard'),
              title: t('global', 'embed'),
              divider: true,
              disabled: !allowEmbedDashboard
            },
            {
              iconName: 'Delete',
              onClick: () => handleOpenDialog(dashboard.id, 'delete_dashboard'),
              title: t('global', 'delete'),
              disabled: dashboards.length === 1
            }
          ]
        : undefined
    }
  })
}
